<script lang="ts">
  import * as Table from '$lib/components/ui/table';
  import { onMount } from 'svelte';
  import type { Writable } from 'svelte/store';
  import SeasonStars from './SeasonStars.svelte';
  import HelpTooltip from '@/Common/HelpTooltip.svelte';
  import { daysByX, formatSignature, loadStats, signatureStats, yearColor, type Metric, type Season, type SeasonDay, type SigKey } from './seasons';
  import { kmToMile } from '@/Util/utils';

  export let seasons: Record<string, Season>;
  export let years: number[];
  // metric 'tl' is card 1 (total load, status, activity totals); otherwise an individual load with its signature.
  export let metric: Metric;
  export let sigKey: SigKey | null = null;
  export let distanceUnits = 'km';
  export let checkpoint: Writable<number | null>;

  $: isTotal = metric === 'tl';
  // Newest season first.
  $: rows = [...years].sort((a, b) => b - a).filter((y) => seasons[String(y)]);
  $: hasCheckpoint = $checkpoint != null;
  // Start / High / End, plus Checkpoint when one is set.
  $: labels = hasCheckpoint ? ['Start', 'High', 'End', 'Checkpoint'] : ['Start', 'High', 'End'];

  const fmt = (v: number) => v.toFixed(1);
  const dim = (day: SeasonDay | undefined) => (day?.future ? 'opacity-50' : '');

  // Each column group is boxed: left/right borders on its edge columns, a bottom border on the last row.
  // Plain CSS classes (see the style block): Tailwind's base styles are scoped to `.twp`, which this page isn't in,
  // so the border utilities don't get a border-style here.
  function box(j: number, count: number, last: boolean) {
    return [j === 0 ? 'grp-l' : '', j === count - 1 ? 'grp-r' : '', last ? 'grp-b' : ''].join(' ');
  }

  function distance(km: number) {
    const value = distanceUnits === 'mi' ? kmToMile(km) : km;
    return `${Math.round(value).toLocaleString()} ${distanceUnits}`;
  }

  // Mirrors SeasonData::addPredictedSignatures: seed breakthrough value + k1 * (TL(day) - TL(seed)).
  $: predictedHelp = `Predicted ${sigKey?.toUpperCase() ?? ''} is estimated from your training load relative to your best performance: `
    + `it starts from your best breakthrough of the season (or the closest one outside it) and rises or falls with how your `
    + `training load on each day compares to your training load at the time of that breakthrough. `
    + `Because it's based on training load, it may not match the fitness signatures of your activities at that time.`;

  // On a phone the table is wider than the card, and Table.Root's wrapper div scrolls it. Fade the right edge while
  // there's more to scroll to, since mobile browsers don't show a scrollbar until you're already scrolling.
  let tableEl: HTMLDivElement;
  let moreRight = false;
  function updateMoreRight() {
    const scroller = tableEl?.firstElementChild as HTMLElement | null;
    moreRight = !!scroller && scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
  }
  onMount(() => {
    const scroller = tableEl.firstElementChild as HTMLElement;
    scroller.addEventListener('scroll', updateMoreRight, { passive: true });
    const ro = new ResizeObserver(updateMoreRight);
    ro.observe(scroller);
    ro.observe(scroller.firstElementChild as HTMLElement);
    return () => {
      scroller.removeEventListener('scroll', updateMoreRight);
      ro.disconnect();
    };
  });

  const percentActive = (s: Season) => (s.elapsed_days > 0 ? `${Math.round((100 * s.active_days) / s.elapsed_days)}%` : '–');
</script>

<!-- No width class here or below: an explicit width (e.g. w-full, which Table.Root also puts on its own wrapper div
     and on the <table> itself) breaks the xl: side-by-side layout - it makes the element's intrinsic size 0, which is
     what the parent grid's max-content table column sizes itself from (see MySeasons.svelte), collapsing it to
     nothing. w-max overrides Table.Root's own w-full on the <table> (cn() keeps the later class); the :global rule
     below undoes width:100% on its wrapper div, which isn't exposed through a prop. -->
<div class="season-table" class:more-right={moreRight} bind:this={tableEl}>
  <Table.Root class="w-max text-xs sm:text-sm">
    <Table.Header>
      <!-- Group labels. Every real heading sits in the second row so they all line up. -->
      <Table.Row class="border-b-0">
        <Table.Head class="year-col px-1.5 whitespace-nowrap text-center h-7 p-0"></Table.Head>
        <Table.Head colspan={labels.length} class="h-7 grp-head bg-muted/40 py-0 text-center text-foreground">Training load</Table.Head>
        {#if isTotal}
          <Table.Head colspan={labels.length} class="h-7 grp-head bg-muted/40 py-0 text-center text-foreground">Training status</Table.Head>
          <Table.Head colspan={4} class="h-7 p-0"></Table.Head>
        {:else if sigKey}
          <Table.Head colspan={labels.length} class="h-7 grp-head bg-muted/40 py-0 text-center text-foreground">
            <span class="inline-flex items-center gap-0.5">
              Predicted {sigKey ? sigKey.toUpperCase() : 'value'}
              <HelpTooltip message={predictedHelp} iconClass="w-5 h-5 text-gray-500" />
            </span>
          </Table.Head>
          <Table.Head class="px-1.5 whitespace-nowrap text-center h-7 p-0"></Table.Head>
        {/if}
      </Table.Row>
      <Table.Row>
        <Table.Head class="year-col px-1.5 whitespace-nowrap text-center align-bottom">Year</Table.Head>
        {#each { length: isTotal || sigKey ? 2 : 1 } as _, g}
          {#each labels as label, j}
            <Table.Head class="px-1.5 whitespace-nowrap text-center align-bottom {box(j, labels.length, false)} {label === 'Checkpoint' ? 'text-blue-600' : ''}">
              {label}
            </Table.Head>
          {/each}
        {/each}
        {#if isTotal}
          <Table.Head class="px-1.5 whitespace-nowrap text-center align-bottom">Distance</Table.Head>
          <Table.Head class="px-1.5 whitespace-nowrap text-center align-bottom">Hours</Table.Head>
          <Table.Head class="px-1.5 whitespace-nowrap text-center align-bottom">Days active</Table.Head>
          <Table.Head class="px-1.5 whitespace-nowrap text-center align-bottom">% days</Table.Head>
        {:else if sigKey}
          <Table.Head class="px-1.5 whitespace-nowrap text-center align-bottom">Breakthrough</Table.Head>
        {/if}
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {#each rows as year, i (year)}
        {@const season = seasons[String(year)]}
        {@const last = i === rows.length - 1}
        {@const load = loadStats(season, metric)}
        {@const sig = sigKey ? signatureStats(season, sigKey) : null}
        {@const cp = $checkpoint == null ? undefined : daysByX(season).get($checkpoint)}
        {@const loadDays = load ? [load.start, load.high, load.end, ...(hasCheckpoint ? [cp] : [])] : []}
        {@const sigDays = sig ? [sig.start, sig.high, sig.end, ...(hasCheckpoint ? [cp] : [])] : []}
        <Table.Row class={last ? 'border-b-0' : ''}>
          <Table.Cell class="year-col px-1.5 whitespace-nowrap text-center font-semibold" style="color: {yearColor(year)}">{year}</Table.Cell>

          <!-- Training load -->
          {#each labels as label, j}
            {@const day = loadDays[j]}
            <Table.Cell class="px-1.5 whitespace-nowrap text-center {box(j, labels.length, last)} {dim(day)} {label === 'Checkpoint' ? 'text-blue-700' : ''}">
              {day ? fmt(day[metric]) : '–'}
            </Table.Cell>
          {/each}

          {#if isTotal}
            <!-- Training status -->
            {#each labels as label, j}
              {@const day = loadDays[j]}
              <Table.Cell class="px-1.5 whitespace-nowrap text-center {box(j, labels.length, last)} {dim(day)}">
                {#if day}<SeasonStars rating={day.ts} size={11} />{:else}–{/if}
              </Table.Cell>
            {/each}
            <Table.Cell class="px-1.5 whitespace-nowrap text-center">{distance(season.distance_km)}</Table.Cell>
            <Table.Cell class="px-1.5 whitespace-nowrap text-center">{season.hours.toFixed(1)}</Table.Cell>
            <Table.Cell class="px-1.5 whitespace-nowrap text-center">{season.active_days}</Table.Cell>
            <Table.Cell class="px-1.5 whitespace-nowrap text-center">{percentActive(season)}</Table.Cell>
          {:else if sigKey}
            <!-- Predicted value -->
            {#each labels as label, j}
              {@const day = sigDays[j]}
              <Table.Cell class="px-1.5 whitespace-nowrap text-center {box(j, labels.length, last)} {dim(day)} {label === 'Checkpoint' ? 'text-blue-700' : ''}">
                {day ? formatSignature(day.sg[sigKey], sigKey) : sig ? '–' : j === 0 ? 'None' : '–'}
              </Table.Cell>
            {/each}
            <Table.Cell class="px-1.5 whitespace-nowrap text-center">
              {#if season.breakthroughs[sigKey]}
                <a class="text-emerald-700 underline" href="/activities/{season.breakthroughs[sigKey]?.path}" target="activity">
                  {formatSignature(season.breakthroughs[sigKey]?.value, sigKey)}
                </a>
              {:else}
                None
              {/if}
            </Table.Cell>
          {/if}
        </Table.Row>
      {/each}
    </Table.Body>
  </Table.Root>
</div>

<style>
  .season-table {
    --group-border: #cbd5e1;
    /* Scrolls horizontally if the table is ever wider than the space the grid gives this column (e.g. many years
       selected on a narrower xl: viewport); Table.Root's own wrapper div already scrolls too, but its width:100%
       (overridden below) makes it exactly as wide as this element, so this is the one that can actually be narrower
       than the table's content. */
    overflow-x: auto;
  }
  /* Table.Root's own wrapper div (<div class="relative w-full overflow-auto">, see $lib/components/ui/table/table.svelte)
     isn't exposed through a prop, so it's overridden here - see the comment above the markup. Both !important:
     tailwind.config.js forces every utility (including that w-full) to !important, so a plain declaration here
     would lose to it. */
  .season-table > :global(div) {
    width: max-content !important;
    max-width: 100% !important;
  }
  .season-table {
    position: relative;
  }
  .season-table.more-right::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 24px;
    pointer-events: none;
    background: linear-gradient(to right, transparent, hsl(var(--card)));
  }
  /* Keeps the year visible while the rest of the row is scrolled sideways on a phone. */
  .season-table :global(.year-col) {
    position: sticky;
    left: 0;
    z-index: 1;
    background: hsl(var(--card));
  }
  .season-table :global(.grp-l) {
    border-left: 1px solid var(--group-border);
  }
  .season-table :global(.grp-r) {
    border-right: 1px solid var(--group-border);
  }
  .season-table :global(.grp-b) {
    border-bottom: 1px solid var(--group-border);
  }
  .season-table :global(.grp-head) {
    border: 1px solid var(--group-border);
    border-bottom: 0;
    border-radius: 6px 6px 0 0;
  }
</style>
